import React, { useContext, useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Select,
  MenuItem,
  Typography,
  Tooltip,
} from '@mui/material';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import { createDossier, updateDossier } from '../../../services/stixify_api.ts';
import LoadingButton from '../../../components/loading_button/index.tsx';
import { useAlert } from '../../../contexts/alert-context.tsx';
import LabelInput from './label-input.tsx';
import { TeamContext } from '../../../contexts/team-context.tsx';

const TLP_LEVELS = [
  'amber',
  'green',
]

interface DossierFormProps {
  open: boolean;
  onClose: () => void;
  onAddEntry: () => void;
  entryData?: any;
}

const DossierForm: React.FC<DossierFormProps> = ({
  open,
  onClose,
  onAddEntry,
  entryData,
}) => {
  const { activeTeam } = useContext(TeamContext);
  const [loading, setLoading] = useState(false)
  const alert = useAlert()
  const [formData, setFormData] = useState<{
    name: string,
    description: string,
    tlp_level: string,
    labels: string[],
  }>({
    name: '',
    description: '',
    tlp_level: '',
    labels: [],
  });

  const [errors, setErrors] = useState<{
    name?: string[],
    description?: string[],
    tlp_level?: string[],
    labels?: string[],
    non_field_errors?: string[],
  }>({})


  useEffect(() => {
    setFormData({
      name: entryData?.name || '',
      description: entryData?.description || '',
      tlp_level: entryData?.tlp_level || '',
      labels: entryData?.labels || [],
    });
    setErrors({})

  }, [open, entryData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };



  const addEntry = async (data: any) => {
    if(!activeTeam) return
    if (entryData) {
      await updateDossier(activeTeam.id, entryData.id, data);
      return
    }
    await createDossier(activeTeam.id || '', {
      ...data
    })
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { ...submitData } = formData
    setLoading(true)
    try {
      await addEntry(submitData);
      onAddEntry();
    } catch (err) {
      if (err?.response?.status === 400) {
        console.log(err?.response?.data?.details, err?.response?.data, 'sxaa')
        setErrors(err?.response?.data?.details || err?.response?.data)
      }
    } finally {
      setLoading(false)
    }
  };

  return (
    <Box>
      <Typography variant='h4'>{entryData ? 'Update a Dossier' : 'Add a Dossier'}</Typography>

      <Typography>Please enter the details of the Dossier you would like to create. Once a Dossier is created you can add Reports to it.</Typography>

      <Box mt={4}>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <strong>Name</strong>
          <Tooltip title='[REQUIRED] The name of the Dossier. Is visible to members of this team and to any user in Stixify if you use the TLP:Green marking. You can enter as many labels as you need.'><HelpCenterIcon></HelpCenterIcon></Tooltip>
        </Box>
        <TextField
          autoFocus
          margin="dense"
          name="name"
          label="Name"
          type="text"
          fullWidth
          value={formData.name}
          onChange={handleChange}
          required
        />
        {errors?.name?.map(error => <Typography sx={{ color: 'red' }}>{error}</Typography>)}
      </Box>
      <Box mt={4}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <strong>Description</strong>
          <Tooltip title='[OPTIONAL] A description of this Dossier. Is visible to members of this team and to any user in Stixify if you use the TLP:Green marking.'><HelpCenterIcon></HelpCenterIcon></Tooltip>
        </Box>

        <TextField
          margin="dense"
          name="description"
          label="Description"
          type="text"
          fullWidth
          value={formData.description}
          onChange={handleChange}
          required
        />
        {errors?.description?.map(error => <Typography sx={{ color: 'red' }}>{error}</Typography>)}
      </Box>

      <Box mt={4}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <strong>Labels</strong>
          <Tooltip title='[OPTIONAL] Labels categorise Dossiers making them easier to search for. Is visible to members of this team and to any user in Stixify if you use the TLP:Green marking. You can enter as many labels as you need by clicking the Add button.'><HelpCenterIcon></HelpCenterIcon></Tooltip>
        </Box>
        <LabelInput
          value={formData.labels}
          onValueChange={(value) => setFormData({ ...formData, labels: value })}></LabelInput>
      </Box>

      <Box sx={{marginBottom: '2rem'}}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <strong>TLP Level</strong>
          <Tooltip title='[REQUIRED] TLP defines who can see the Dossier inside Stixify and the types of Reports that can be added to it. Dossiers marked TLP:Green are visible to all Stixify users and can only contain Reports marked TLP:Green. Dossiers marked TLP:Amber are only visible to members of your team and can contain Reports marked TLP:Green or TLP:Amber.'><HelpCenterIcon></HelpCenterIcon></Tooltip>
        </Box>
        <Select
          sx={{ width: '100%' }}
          label="TLP Level"
          name="tlp_level"
          value={formData.tlp_level}
          onChange={(e) => setFormData({ ...formData, tlp_level: e.target.value })}
        >
          {TLP_LEVELS.map((extraction) => (
            <MenuItem key={extraction} value={extraction}>{extraction}</MenuItem>
          ))}
        </Select>
        {errors?.tlp_level?.map(error => <Typography sx={{ color: 'red' }}>{error}</Typography>)}
      </Box>

      {errors?.non_field_errors?.map(error => <Typography sx={{ color: 'red' }}>{error}</Typography>)}
      <Button variant='contained' sx={{ textTransform: 'uppercase' }} onClick={onClose} color="secondary">
        Cancel
      </Button>
      <LoadingButton isLoading={loading} variant='contained' sx={{ textTransform: 'uppercase', marginLeft: '3rem' }} onClick={handleSubmit} color="primary">
        {entryData ? 'Update' : 'Add'}
      </LoadingButton>
    </Box >
  );
};

export default DossierForm;
